import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Mic, Square, PhoneOff, Loader2, X, Bot, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sandbox WebRTC simulator: talks to the Aayush AI loop via the browser mic.
// Bypasses real telephony entirely (parallel to the Exotel/Plivo pipeline).
export const SimulateCallModal = ({ lead, token, onClose }) => {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [phase, setPhase] = useState('connecting'); // connecting | ready | recording | thinking | ended
  const [turns, setTurns] = useState([]);
  const [summary, setSummary] = useState('');
  const [recUrl, setRecUrl] = useState('');
  const simIdRef = useRef('');
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const processorRef = useRef(null);
  const pcmChunksRef = useRef([]);
  const recRateRef = useRef(16000);

  const playAudio = (b64) => {
    if (!b64) return;
    try { new Audio(`data:audio/wav;base64,${b64}`).play().catch(() => {}); } catch {}
  };

  // Build a 16-bit mono WAV blob from captured Float32 PCM (no MediaRecorder/webm).
  const encodeWav = (float32, rate) => {
    const len = float32.length;
    const buf = new ArrayBuffer(44 + len * 2);
    const view = new DataView(buf);
    const wr = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
    wr(0, 'RIFF'); view.setUint32(4, 36 + len * 2, true); wr(8, 'WAVE');
    wr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
    view.setUint16(22, 1, true); view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true);
    view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    wr(36, 'data'); view.setUint32(40, len * 2, true);
    let off = 44;
    for (let i = 0; i < len; i++) { const s = Math.max(-1, Math.min(1, float32[i])); view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true); off += 2; }
    return new Blob([view], { type: 'audio/wav' });
  };

  const start = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/voice-simulator/start`, {
        method: 'POST', headers, body: JSON.stringify({ phone: lead.phone || '', lead_id: lead.id, visitor_name: lead.name || '' }) });
      const d = await res.json();
      if (!d.success) { toast.error('Simulator start नहीं हुआ'); onClose(); return; }
      simIdRef.current = d.sim_id;
      setTurns([{ role: 'aayush', text: d.reply_text }]);
      playAudio(d.audio_base64);
      // mic permission
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      setPhase('ready');
    } catch (e) {
      toast.error('माइक permission/connection चाहिए: ' + e.message);
      setPhase('ready'); // allow typed fallback even without mic
    }
  }, [lead]);

  useEffect(() => { start(); return () => { try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {} }; }, [start]);

  const sendTurn = async ({ audio_base64, text }) => {
    setPhase('thinking');
    try {
      const res = await fetch(`${API_URL}/api/voice-simulator/turn`, {
        method: 'POST', headers, body: JSON.stringify({ sim_id: simIdRef.current, audio_base64, audio_codec: 'wav', text }) });
      const d = await res.json();
      if (!d.success) { toast.error(d.error || 'समझ नहीं आया, फिर बोलें'); setPhase('ready'); return; }
      setTurns(t => [...t, { role: 'user', text: d.user_text }, { role: 'aayush', text: d.reply_text }]);
      if (d.handoff) toast.message('↪ Senior Executive handoff', { description: `Transfer: ${d.transfer_to}` });
      playAudio(d.audio_base64);
    } catch (e) { toast.error('Turn fail: ' + e.message); }
    finally { setPhase('ready'); }
  };

  const startRecording = async () => {
    if (!streamRef.current) { toast.error('माइक उपलब्ध नहीं — नीचे type करके test करें'); return; }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    try { await ctx.resume(); } catch {}  // Chrome/iOS start suspended until a gesture
    audioCtxRef.current = ctx;
    recRateRef.current = ctx.sampleRate;
    pcmChunksRef.current = [];
    const source = ctx.createMediaStreamSource(streamRef.current);
    const proc = ctx.createScriptProcessor(4096, 1, 1);
    const mute = ctx.createGain(); mute.gain.value = 0;  // capture without speaker echo
    proc.onaudioprocess = (e) => { pcmChunksRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0))); };
    source.connect(proc); proc.connect(mute); mute.connect(ctx.destination);
    processorRef.current = { proc, source, mute };
    setPhase('recording');
  };

  const stopRecording = () => {
    const p = processorRef.current;
    if (!p) return;
    try { p.proc.disconnect(); p.source.disconnect(); p.mute?.disconnect(); audioCtxRef.current?.close(); } catch {}
    processorRef.current = null;
    // flatten captured PCM → guard against empty/too-short clips
    const chunks = pcmChunksRef.current;
    const total = chunks.reduce((n, c) => n + c.length, 0);
    if (total < 1200) {  // ~nothing captured (mic muted / too quick)
      toast.error('कुछ सुनाई नहीं दिया — थोड़ा देर बोलें या नीचे type करें');
      setPhase('ready'); return;
    }
    const flat = new Float32Array(total);
    let off = 0; chunks.forEach(c => { flat.set(c, off); off += c.length; });
    const blob = encodeWav(flat, recRateRef.current);
    const reader = new FileReader();
    reader.onloadend = () => sendTurn({ audio_base64: String(reader.result).split(',')[1] });
    reader.readAsDataURL(blob);
  };

  const [typed, setTyped] = useState('');
  const sendTyped = () => { if (typed.trim()) { sendTurn({ text: typed.trim() }); setTyped(''); } };

  const endCall = async () => {
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
    try {
      const res = await fetch(`${API_URL}/api/voice-simulator/end`, {
        method: 'POST', headers, body: JSON.stringify({ sim_id: simIdRef.current }) });
      const d = await res.json();
      setSummary(d.summary || '');
      setRecUrl(`${API_URL}/api/voice-simulator/recording/${simIdRef.current}`);
      setPhase('ended');
    } catch (e) { toast.error('End fail: ' + e.message); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="sim-call-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 bg-zinc-900 text-white flex items-center justify-between">
          <div>
            <p className="font-semibold text-sm flex items-center gap-2"><Bot className="w-4 h-4" /> 🎙️ WebRTC Simulator (Sandbox)</p>
            <p className="text-[11px] text-zinc-400">{lead.name || 'Lead'} · {lead.phone || '—'}</p>
          </div>
          <button onClick={onClose} data-testid="sim-close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Transcript */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-zinc-50" data-testid="sim-transcript">
          {phase === 'connecting' && <div className="flex items-center justify-center py-10 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> connecting…</div>}
          {turns.map((t, i) => (
            <div key={i} className={`flex gap-2 ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${t.role === 'user' ? 'bg-emerald-600 text-white' : 'bg-white border border-zinc-200 text-zinc-800'}`}>
                <span className="text-[10px] opacity-60 flex items-center gap-1">{t.role === 'user' ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />}{t.role === 'user' ? 'आप' : 'आयुष'}</span>
                {t.text}
              </div>
            </div>
          ))}
          {phase === 'thinking' && <div className="text-xs text-zinc-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> आयुष सोच रहा है…</div>}
        </div>

        {/* Controls */}
        {phase === 'ended' ? (
          <div className="p-4 border-t border-zinc-200 space-y-3" data-testid="sim-ended">
            <p className="text-xs text-zinc-500">📋 Summary</p>
            <p className="text-sm text-zinc-700 max-h-24 overflow-auto">{summary || '—'}</p>
            <audio controls className="w-full h-9" src={recUrl} data-testid="sim-recording-audio" />
            <button onClick={onClose} className="w-full h-10 rounded-lg bg-zinc-900 text-white text-sm font-semibold" data-testid="sim-done">बंद करें</button>
          </div>
        ) : (
          <div className="p-4 border-t border-zinc-200 space-y-3">
            <div className="flex items-center gap-2">
              {phase === 'recording' ? (
                <button onClick={stopRecording} data-testid="sim-stop-record"
                  className="flex-1 h-11 rounded-lg bg-rose-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 animate-pulse">
                  <Square className="w-4 h-4" /> रोकें व भेजें
                </button>
              ) : (
                <button onClick={startRecording} disabled={phase !== 'ready'} data-testid="sim-record"
                  className="flex-1 h-11 rounded-lg bg-emerald-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  <Mic className="w-4 h-4" /> 🎤 बोलें
                </button>
              )}
              <button onClick={endCall} data-testid="sim-end-call"
                className="h-11 px-4 rounded-lg bg-zinc-800 text-white text-sm font-semibold inline-flex items-center gap-2">
                <PhoneOff className="w-4 h-4" /> End
              </button>
            </div>
            {/* typed fallback (no mic) */}
            <div className="flex items-center gap-2">
              <input value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendTyped()}
                placeholder="या यहाँ type करके test करें…" data-testid="sim-typed-input"
                className="flex-1 h-9 px-3 rounded-lg border border-zinc-200 text-sm" disabled={phase === 'thinking'} />
              <button onClick={sendTyped} disabled={phase === 'thinking' || !typed.trim()} data-testid="sim-typed-send"
                className="h-9 px-3 rounded-lg bg-zinc-900 text-white text-xs font-semibold disabled:opacity-50">भेजें</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimulateCallModal;
